import { useState, useRef, useEffect } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Package } from "lucide-react";
import { useProductsList } from "../hooks/useProductsList";
import { submitReviewDecision } from "../services/reviewsService";
import { fetchProductReviewDetails } from "../services/productsService";
import { useAuth } from "../context/AuthContext";
import { can, AGE_GROUPS_DISPLAY } from "../data/constants";
import RejectModal from "../components/RejectModal";
import ConfirmModal from "../components/ConfirmModal";
import ApproveModal from "../components/ApproveModal";
import ProductViewDrawer from "../components/ProductViewDrawer";
import { parseStageScores, scoreTone, SCORE_STAGE_RANGES } from "../components/ProductScoreStages";

// ── SVG assets from productlist folder ──────────────────────────────────────
import SearchIcon from "../assets/product/productlist/Search.svg";
import DropdownIcon from "../assets/product/productlist/dropdown.svg";
import FilterIcon from "../assets/product/productlist/filter.svg";
import ActionDotsIcon from "../assets/product/productlist/action_buttons.svg";
import EyeIcon from "../assets/product/productlist/eye.svg";
import EditIcon from "../assets/product/productlist/edit.svg";
import ApproveIcon from "../assets/product/productlist/approve.svg";
import RejectIcon from "../assets/product/productlist/reject.svg";

const PER = 10;

// The full set of product_score_v2 stages shown per list row, in display
// order — I2/I3 and L1 are omitted since they aren't distinct editable
// targets elsewhere in the admin UI (I1 alone represents the 0–24 month
// window, matching the edit page's score cards). Labels come from the same
// SCORE_STAGE_RANGES age ranges the detail page's stage cards show.
const SCORE_STAGE_DISPLAY_KEYS = [/* "I1", */ "C1", "C2", "C3", "C4", "M1"];

/** Resolves every product_score_v2 stage for one list row, score-or-not. */
function rowStageScores(p) {
  const { stageScores } = parseStageScores(p);
  const byKey = new Map(stageScores.map((st) => [st.key, st]));

  return SCORE_STAGE_DISPLAY_KEYS.map((key) => {
    const st = byKey.get(key);
    const label = SCORE_STAGE_RANGES[key] || key;
    const hasScore = st && st.score !== null && st.score !== undefined && st.score !== "";
    const scoreNum = hasScore ? Number(st.score) : null;
    return { key, label, hasScore, scoreNum, tone: scoreTone(hasScore ? scoreNum : null) };
  });
}

// ── Status badge — exact colours from spec ───────────────────────────────────
function StatusBadgeNew({ status }) {
  if (!status) return null;
  const s = status.toLowerCase();

  if (s === "approved" || s === "approve") {
    return (
      <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-semibold bg-[#0B61721A] border border-[#0B617233] text-[#0B6172] whitespace-nowrap">
        Approved
      </span>
    );
  }

  if (s === "rejected" || s === "reject") {
    return (
      <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-semibold bg-[#FF00001A] border border-[#FF000033] text-[#dc2626] whitespace-nowrap">
        Rejected
      </span>
    );
  }

  if (s === "pending") {
    return (
      <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-semibold bg-[#FFF3CD] border border-[#FBBF24] text-[#92400E] whitespace-nowrap">
        Pending
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-semibold bg-gray-100 border border-gray-300 text-gray-500 whitespace-nowrap capitalize">
      {status}
    </span>
  );
}

// ── Pagination ───────────────────────────────────────────────────────────────
function PaginationNew({ page, total, perPage, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;

  const getPages = () => {
    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const delta = 1;
    const range = [];
    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i);
    }
    const result = [1];
    if (range[0] > 2) result.push("...");
    range.forEach((p) => result.push(p));
    if (range[range.length - 1] < totalPages - 1) result.push("...");
    result.push(totalPages);
    return result;
  };

  const pages = getPages();

  return (
    <div className="flex justify-between items-center px-[18px] py-[14px] border-t border-[#0000001A]">
      <p className="text-sm text-[#777]">
        Showing <strong className="text-[#333]">
          {Math.min((page - 1) * perPage + 1, total)}-
          {Math.min(page * perPage, total)}
        </strong>{" "}
        of <strong className="text-[#333]">{total}</strong> products
      </p>

      <div className="flex items-center gap-1.5">
        {/* Prev */}
        <button
          className="w-9 h-9 rounded-lg border border-[#0000001A] bg-white cursor-pointer flex items-center justify-center text-sm text-[#444] font-medium disabled:opacity-30"
          disabled={page === 1}
          onClick={() => onChange(Math.max(1, page - 1))}
        >
          ‹
        </button>

        {pages.map((p, idx) =>
          p === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="w-9 text-center text-[#888] text-sm"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm ${p === page
                ? "bg-[#0B6172] text-white border-none font-bold"
                : "border border-[#0000001A] bg-white text-[#444] font-medium cursor-pointer"
                }`}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          className="w-9 h-9 rounded-lg border border-[#0000001A] bg-white cursor-pointer flex items-center justify-center text-sm text-[#444] font-medium disabled:opacity-30"
          disabled={page === totalPages}
          onClick={() => onChange(Math.min(totalPages, page + 1))}
        >
          ›
        </button>
      </div>
    </div>
  );
}

// ── Three-dot action menu ────────────────────────────────────────────────────
function ActionMenu({ product, onView, onEdit, onApprove, onReject, userRole }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-lg border-none bg-transparent cursor-pointer flex items-center justify-center hover:bg-gray-100 transition-colors"
      >
        <img src={ActionDotsIcon} alt="actions" className="w-[22px] h-[22px]" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-10 z-20 bg-white border border-[#0000001A] rounded-[10px] shadow-[0_0_20px_0_#0000001A] min-w-[130px] overflow-hidden"
        >
          {[
            {
              label: "View",
              color: "text-gray-700",
              icon: EyeIcon,
              onClick: () => { setOpen(false); onView(); },
              show: true,
            },
            {
              label: "Edit",
              color: "text-gray-700",
              icon: EditIcon,
              onClick: () => { setOpen(false); onEdit(); },
              show: can(userRole, "edit_p") && product.status !== "failed",
            },
            {
              label: "Approve",
              color: "text-[#0B6172]",
              icon: ApproveIcon,
              onClick: () => { setOpen(false); onApprove(); },
              show: can(userRole, "approve") && product.status !== "approved" && product.status !== "failed",
            },
            {
              label: "Reject",
              color: "text-red-600",
              icon: RejectIcon,
              onClick: () => { setOpen(false); onReject(); },
              show: can(userRole, "reject") && product.status !== "rejected" && product.status !== "failed",
            },
          ]
            .filter((item) => item.show)
            .map((item) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] bg-transparent cursor-pointer hover:bg-gray-50 transition-colors border-t border-[#0000001A] first:border-t-0 ${item.color}`}
              >
                <img src={item.icon} alt={item.label} className="w-4 h-4 shrink-0" />
                {item.label}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function ProductsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { products, setProducts, addToast } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";
  const statusF = searchParams.get("status") || "all";
  const pg = Number(searchParams.get("page")) || 1;
  // Carried through detail/edit pages so "Back" restores the same filters and page.
  const listState = { listSearch: searchParams.toString() };

  const setSearch = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set("q", value); else next.delete("q");
      next.delete("page");
      return next;
    }, { replace: true });
  };
  const setStatusF = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== "all") next.set("status", value); else next.delete("status");
      next.delete("page");
      return next;
    }, { replace: true });
  };
  const setPg = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== 1) next.set("page", String(value)); else next.delete("page");
      return next;
    }, { replace: true });
  };

  const [viewP, setViewP] = useState({ open: false, product: null });
  const [rejectM, setRejectM] = useState({ open: false, id: null });
  const [confirmM, setConfirmM] = useState({ open: false });
  const [approveM, setApproveM] = useState({ open: false, id: null, name: "" });
  const [deciding, setDeciding] = useState(false);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { loading, total } = useProductsList(setProducts, addToast, statusF, pg, PER, search);
  const isLoading = loading || deciding;

  // ── Approve ───────────────────────────────────────────────────────────────
  const doApprove = async (id, notes = "", ageGroup = "") => {
    const item = products.find((x) => (x.product_id || x.id) === id);
    if (!item) return;
    setDeciding(true);
    try {
      const details = await fetchProductReviewDetails(id);
      const reviewId = details?.id;
      if (!reviewId) { addToast("Failed to fetch review task details.", "error"); return; }
      await submitReviewDecision(reviewId, {
        decision: "approved",
        review_notes: notes || undefined,
        product_name: item.name || details.submission?.product_name,
        brand_name: item.brand || details.submission?.brand_name,
        category: item.category || details.submission?.category || "General",
        age_group: ageGroup || undefined,
      });
      setProducts((prev) =>
        prev.map((x) =>
          (x.product_id || x.id) === id
            ? { ...x, status: "approved", approvedBy: user.email, approvedDate: new Date().toISOString().split("T")[0], rejectReason: null, ageGroup: ageGroup }
            : x
        )
      );
      addToast("Product approved ✓", "success");
    } catch (err) {
      addToast(err.message || "Failed to approve product", "error");
    } finally {
      setDeciding(false);
    }
  };

  // ── Reject ────────────────────────────────────────────────────────────────
  const doReject = async (id, reason) => {
    const item = products.find((x) => (x.product_id || x.id) === id);
    if (!item) return;
    if (reason.length < 20) { addToast("Rejection notes must be at least 20 characters.", "error"); return; }
    setDeciding(true);
    try {
      const details = await fetchProductReviewDetails(id);
      const reviewId = details?.id;
      if (!reviewId) { addToast("Failed to fetch review task details.", "error"); return; }
      await submitReviewDecision(reviewId, {
        decision: "rejected",
        review_notes: reason,
        product_name: item.name || details.submission?.product_name,
        brand_name: item.brand || details.submission?.brand_name,
        category: item.category || details.submission?.category || "General",
      });
      setProducts((prev) =>
        prev.map((x) =>
          (x.product_id || x.id) === id
            ? { ...x, status: "rejected", rejectReason: reason, approvedBy: null, approvedDate: null }
            : x
        )
      );
      addToast("Product rejected ✓", "info");
    } catch (err) {
      addToast(err.message || "Failed to reject product", "error");
    } finally {
      setDeciding(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4">

      {/* ── Header card ──────────────────────────────────────────────────── */}
      {/* <div className="bg-[#FFFFFFB2] border border-[#0000001A] rounded-[20px] shadow-[0_4px_32px_0_#0000000A] backdrop-blur-[20px] px-6 py-5 flex items-center justify-between">
        <div>
          <h1 className="font-[800] text-2xl text-gray-900 m-0 font-['Google_Sans_Flex',sans-serif]">
            Product Master
          </h1>
          <p className="text-[13px] text-[#0B6172] mt-1">
            {total} product{total !== 1 ? "s" : ""} in the database
          </p>
        </div>
      </div> */}

      {/* ── Filters + table card ─────────────────────────────────────────── */}
      <div className="bg-[#FFFFFFB2] border border-[#0000001A] rounded-[20px] overflow-hidden">
        {/* ── Top bar: search + dropdowns + filter ─────────────────────── */}
        <div className="flex items-center gap-3 px-[18px] py-4 flex-wrap">
          {/* Search */}
          <div className="h-[42px] bg-white border border-[#0000001A] rounded-lg flex items-center gap-2 px-3.5 cursor-pointer text-sm text-[#444] flex-1 min-w-[220px]">
            <img src={SearchIcon} alt="search" className="w-4 h-4 shrink-0" />
            <input
              type="text"
              placeholder="Search by Name, GTIN, Brand"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 border-none outline-none bg-transparent text-sm text-gray-600"
            />
          </div>

          {/* All Statuses */}
          <div className="h-[42px] bg-white border border-[#0000001A] rounded-lg flex items-center gap-2 px-3.5 relative min-w-[130px]">
            <select
              value={statusF}
              onChange={(e) => setStatusF(e.target.value)}
              className="w-full bg-transparent border-none outline-none appearance-none pr-6 text-sm text-[#444] font-medium cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <img src={DropdownIcon} alt="dropdown" className="w-3 h-2 absolute right-3 pointer-events-none" />
          </div>

          {/* Filter icon button */}
          {/* <div className="h-[42px] bg-white border border-[#0000001A] rounded-lg flex items-center gap-2 px-3 cursor-pointer text-sm text-[#444] w-[42px] justify-center">
            <img src={FilterIcon} alt="filter" className="w-[18px] h-3" />
          </div> */}
        </div>

        {/* Horizontal scroll container for the table */}
        <div className="overflow-x-auto w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="min-w-[1450px]">
            {/* ── Table header row ─────────────────────────────────────────── */}
            <div className="grid grid-cols-[2.4fr_1fr_1fr_4.2fr_1fr_0.8fr] bg-[#5E5E5E14] border border-b-0 border-[#0000001A] rounded-t-lg px-[18px] py-[13px] mx-[18px]">
              {["PRODUCT", "BRAND", "AGE GROUP", "HEALTH SCORES BY AGE", "STATUS", "ACTIONS"].map((col, i) => (
                <div
                  key={col}
                  className={`text-xs font-bold text-gray-500 uppercase tracking-wider ${i === 5 ? "text-right" : "text-left"
                    }`}
                >
                  {col}
                </div>
              ))}
            </div>

            {/* ── Table rows ───────────────────────────────────────────────── */}
            <div className="px-[18px] pt-2.5 pb-1.5 flex flex-col gap-2.5 overflow-y-auto max-h-[calc(100vh-350px)] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <div className="w-7 h-7 border-2 border-[#DBF5F0] border-t-[#0B6172] rounded-full animate-spin" />
                  <p className="text-[13px] text-[#0B6172] font-semibold">
                    {deciding ? "Processing decision…" : "Loading…"}
                  </p>
                </div>
              ) : products.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
                  <Package size={36} color="#A4E5E0" />
                  <p className="font-bold text-gray-500 text-[15px]">No products found</p>
                  <p className="text-[13px]">Try adjusting your search or filters</p>
                </div>
              ) : (
                products.map((p) => {
                  const stageScoreList = rowStageScores(p);

                  return (
                  <div
                    key={p.id}
                    className="grid grid-cols-[2.4fr_1fr_1fr_4.2fr_1fr_0.8fr] items-center bg-white border border-[#0000001A] rounded-lg px-[18px] py-3.5"
                  >
                    {/* Product: image + name + GTIN */}
                    <div className="flex items-center gap-3.5">
                      <div className="w-[50px] h-[50px] rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0 overflow-hidden">
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt={p.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-[22px]">📦</span>
                        )}
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-gray-900 m-0 leading-tight">
                          {p.name}
                        </p>
                        <p className="text-[12px] text-gray-400 mt-1">
                          GTIN: {p.gtin || "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Brand */}
                    <div className="text-[13px] font-bold text-gray-750 uppercase tracking-wider">
                      {p.brand || "—"}
                    </div>

                    {/* Age Group */}
                    <div className="text-[13px] text-gray-700">
                      {AGE_GROUPS_DISPLAY[p.ageGroup] || p.ageGroup || "—"}
                    </div>

                    {/* Health scores per age range (product_score_v2) */}
                    <div className="flex flex-wrap gap-1.5">
                      {stageScoreList.map((s) => (
                        <div
                          key={s.key}
                          title={`${s.label}: ${s.hasScore ? s.scoreNum : "Not scored"}`}
                          className="flex flex-col items-center px-1.5 py-1 rounded-md leading-tight"
                          style={{ background: s.hasScore ? `${s.tone.color}14` : "#f3f4f6" }}
                        >
                          <span className="text-[9.5px] font-semibold text-gray-500 whitespace-nowrap">{s.label}</span>
                          <span className="text-[12px] font-bold" style={{ color: s.hasScore ? s.tone.color : "#9ca3af" }}>
                            {s.hasScore ? (Number.isInteger(s.scoreNum) ? s.scoreNum : s.scoreNum.toFixed(1)) : "—"}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Status badge */}
                    <div>
                      <StatusBadgeNew status={p.status} />
                    </div>

                    {/* Actions */}
                    <div className="text-right">
                      <ActionMenu
                        product={p}
                        userRole={user.role}
                        onView={() => navigate(`/products/${p.product_id || p.id}`, { state: listState })}
                        onEdit={() => navigate(`/products/${p.product_id || p.id}/edit`, { state: listState })}
                        onApprove={() => setApproveM({ open: true, id: p.product_id || p.id, name: p.name })}
                        onReject={() => setRejectM({ open: true, id: p.product_id || p.id })}
                      />
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── Pagination ───────────────────────────────────────────────── */}
        {total > PER && (
          <PaginationNew page={pg} total={total} perPage={PER} onChange={setPg} />
        )}
      </div>

      {/* ── View drawer ─────────────────────────────────────────────────── */}
      <ProductViewDrawer
        open={viewP.open}
        product={viewP.product}
        onClose={() => setViewP({ open: false, product: null })}
        userRole={user.role}
        onEdit={() => viewP.product && navigate(`/products/${viewP.product.product_id || viewP.product.id}/edit`, { state: listState })}
        onApprove={() => viewP.product && setApproveM({ open: true, id: viewP.product.product_id || viewP.product.id, name: viewP.product.name })}
        onReject={() => viewP.product && setRejectM({ open: true, id: viewP.product.product_id || viewP.product.id })}
      />

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <RejectModal
        open={rejectM.open}
        onClose={() => setRejectM({ open: false, id: null })}
        onReject={(r) => doReject(rejectM.id, r)}
      />
      <ApproveModal
        open={approveM.open}
        productName={approveM.name}
        onClose={() => setApproveM({ open: false, id: null, name: "" })}
        onApprove={(notes) => doApprove(approveM.id, notes)}
      />
      <ConfirmModal {...confirmM} onClose={() => setConfirmM({ open: false })} />
    </div>
  );
}
